/**
 * API service for managing user projects
 * 
 * Projects store the complete state of a traffic control plan
 * including signs, workzones, arrows, delineators, and annotations.
 */

import axios from 'axios';
import type { 
  Project, 
  ProjectListItem, 
  CreateProjectRequest, 
  UpdateProjectRequest,
  ProjectData 
} from '../types/project';
import type { AnnotationTemplate } from '../types/annotations';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Get all projects for the current user
 */
export const getUserProjects = async (authToken: string): Promise<ProjectListItem[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/projects/`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    return response.data.projects || [];
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

/**
 * Get a specific project with full data
 */
export const getProject = async (authToken: string, projectId: string): Promise<Project> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/projects/${projectId}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    return response.data.project;
  } catch (error) {
    console.error('Error fetching project:', error);
    throw error;
  }
};

/**
 * Create a new project
 */
export const createProject = async (
  authToken: string,
  projectData: CreateProjectRequest
): Promise<{ id: string }> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/projects/`,
      projectData,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return { id: response.data.id };
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

/**
 * Update an existing project (save)
 */
export const updateProject = async (
  authToken: string,
  projectId: string,
  updates: UpdateProjectRequest
): Promise<{ updated_at: string }> => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/projects/${projectId}`,
      updates,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return { updated_at: response.data.updated_at };
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

/**
 * Delete a project
 */
export const deleteProject = async (authToken: string, projectId: string): Promise<void> => {
  try {
    await axios.delete(`${API_BASE_URL}/api/projects/${projectId}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

/**
 * Rename a project
 */
export const renameProject = async (
  authToken: string,
  projectId: string,
  newName: string
): Promise<void> => {
  try {
    await axios.post(
      `${API_BASE_URL}/api/projects/${projectId}/rename`,
      { name: newName },
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error) {
    console.error('Error renaming project:', error);
    throw error;
  }
};

// ============ Annotation Templates ============

/**
 * Get user's saved annotation templates
 */
export const getAnnotationTemplates = async (authToken: string): Promise<AnnotationTemplate[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/projects/templates/annotations`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    return response.data.templates || [];
  } catch (error) {
    console.error('Error fetching annotation templates:', error);
    throw error;
  }
};

/**
 * Save a new annotation template
 */
export const saveAnnotationTemplate = async (
  authToken: string,
  template: Omit<AnnotationTemplate, 'id' | 'isBuiltIn' | 'createdAt' | 'updatedAt'>
): Promise<{ id: string }> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/projects/templates/annotations`,
      template,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return { id: response.data.id };
  } catch (error) {
    console.error('Error saving annotation template:', error);
    throw error;
  }
};

/**
 * Delete an annotation template
 */
export const deleteAnnotationTemplate = async (
  authToken: string,
  templateId: string
): Promise<void> => {
  try {
    await axios.delete(`${API_BASE_URL}/api/projects/templates/annotations/${templateId}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
  } catch (error) {
    console.error('Error deleting annotation template:', error);
    throw error;
  }
};
